import { StatusBadgeType } from "../../../components/statusBadge/StatusBadge";
import { ClientTypeEnum } from "../../../helpers/constants";
import { FilterProps } from "../../../pages/clientSearch/modelsAndSchemas/Models";
import {
  BillingConfigurationItem,
  Country,
  DocumentType,
  DocumentTypeGetAPI,
  InheritDocumentsResultFormKey,
  ProgramTemplate,
  Resource,
} from "../../../pages/createClient/modelsAndSchemas/Models";
import {
  ContactPreferences,
  LanguagePreference,
} from "../../../pages/participants/components/modelsAndSchemas/Models";
import { IdNameList, Locale } from "../common/models";

export interface ClientSearchParams {
  companyname: string;
  clientcode: string;
  companyfilter: string;
  clientfilter: string;
  cityfilter: string;
  addresscountry: string;
  city: string;
  status?: string;
}

export interface MenuListParams {
  id?: string;
  name?: string;
}

export type SearchPageName =
  | "ClientSearch"
  | "ProgramTemplateSearch"
  | "IndividualsSearch"
  | "ProgramSearch"
  | "GenericAccountSearch"
  | "OfficeSearch"
  | "CoachPortalMyCaseLoadsSearch"
  | "CoachPortalAllParticipantsSearch"
  | "ProjectSearch"
  | "GenericParticipantsSearch"
  | "NisParticipantSearch"
  | null;

export interface SaveSearchFilterParams {
  id?: string;
  name: string;
  savedSearchPageName?: number;
  filters: FilterProps[];
}

export interface CreateClientProfileParams {
  clientName: string;
  parentClientId?: string;
  englishName?: string;
  clientTypeId: string;
  industryId: string;
  localClientRelationshipOwnerId?: string;
  addressCountryId: string;
  addressCountryName: string;
  billingCountryId?: string;
  billingCountryName?: string;
  vatNumber?: string;
  dedicatedProjectManagerId?: string;
  originatorId?: string;
  originDate?: string;
  businessIdentificationNumber?: string;
  salesOfficeId: string;
}

export interface CreateClientMailingAddressParams {
  address1: string;
  address2?: string;
  address3?: string;
  address4?: string;
  address5?: string;
  countryId?: string;
  stateId?: string;
  city: string;
  postalCode: string;
}

export interface InvoicingLanguage {
  id: string;
  name: string;
  code: string;
}

export interface CreateClientBillingDetailsParams {
  hasConsolidatedInvoicing?: boolean;
  isActiveCompany: boolean;
  isRebateClient?: boolean;
  hasEarlyPayDiscount?: boolean;
  earlyPayDays?: number;
  earlyPayPercentage?: number;
  invoiceLanguageId?: string;
  notes?: string;
  invoiceLanguage?: InvoicingLanguage;
  billingConfigurations: BillingConfigurationItem[];
}

export interface CreateClientCombineParams {
  profile: CreateClientProfileParams;
  mailingAddress: CreateClientMailingAddressParams;
}

export interface CreateClientParams {
  client: CreateClientCombineParams;
  status?: string;
}

export interface UpdateClientParams extends BillingDetailsParams {
  client: CreateClientCombineParams;
}

export interface BillingDetailsParams {
  clientId: string;
  billingDetails: CreateClientBillingDetailsParams;
}

export interface BillingDetailsAndConfigurationResponse {
  id: string;
  billingDetails: CreateClientBillingDetailsParams;
}

export interface CreateClient {
  clientCode: string;
  id: string;
  clientTypeId?: string;
  clientName?: string;
  clientBillingCountryId?: string;
}

export interface EditClientState {
  isEdit: boolean;
}

export interface TagRemoveParentClientParams {
  clientId: string;
  parentClientId: string | null;
}

export interface CreateClientResponse {
  data: CreateClient;
}

export interface GetClientDetailsResponse {
  profile: ProfileResponse;
  mailingAddress: MailingAddressResponse;
  isEditable?: boolean;
}

export interface ProfileResponse {
  id: string;
  clientCode: string;
  clientName: string;
  parentClientId?: string;
  parentClientName?: string;
  parentClientCode?: string;
  englishName?: string;
  clientTypeId: string;
  industryId: string;
  industryName: string;
  localClientRelationshipOwnerId: string;
  addressCountryId: string;
  addressCountryName: string;
  billingCountryId: string;
  billingCountryName: string;
  vatNumber?: string;
  businessIdentificationNumber?: string;
  dedicatedProjectManagerId?: string;
  originDate: string;
  isParentClientRemovalAllowed: boolean;
  salesOffice: IdNameList;
}

export interface MailingAddressResponse {
  id: string;
  address1: string;
  countryName: string;
  city: string;
  address2?: string;
  address3?: string;
  countryId?: string;
  stateId?: string;
  postalCode?: string;
}

export interface Item {
  id: string;
  idSource: string;
  geoScopeName?: string;
  key: string;
  active: boolean;
  title: string;
  standardCodes: StandardCode[];
}

export interface StandardCode {
  itemCodeStandardName: string;
  standardCodeValue: string;
}

export interface CountryListResponse {
  key: string;
  active: boolean;
  items: Item[];
}

export interface ContactProfileParams {
  prefixId?: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  telephoneCountryCode?: string;
  telephoneNumber?: string;
  telephoneISOCode?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  mobileISOCode?: string;
  email?: string;
  workEmail: string;
  surveyLanguageId: string;
  status: string;
}

export interface ContactDetailsParams {
  contactTypeId?: string[];
  isSurveyContact?: boolean;
}
export interface AddNewContactParams {
  clientId: string;
  contactProfile: ContactProfileParams;
  contactDetails: ContactDetailsParams;
}

export interface DeleteContactParams {
  clientId: string;
  contactId: string;
}
export interface GetContactParams {
  clientId: string;
  contactId: string;
}

export interface ContactUsedByClients {
  clientProfileId: string;
  clientName: string;
  clientCode: string;
  countryId: string;
  countryName: string;
  isParent: boolean;
}

export interface GetUpdateContactModel extends AddNewContactParams {
  contactId: string;
  usedByClients?: ContactUsedByClients[];
}
export interface GetContactModel {
  data: GetUpdateContactModel;
}

export interface ErrorModel {
  errorCode: string;
  message?: string;
}

export interface AddNewContactResponse {
  data: {
    id: string;
  };
}

type ContactType = "hr" | "billing" | "manager" | "other";
export interface ContactListParams {
  clientId: string;
  contactType: ContactType;
}

export interface DeleteDocumentParams {
  documentId: string;
}

export interface UpdateStatusContractDocumentParams {
  documentId: string;
  status: string;
  cancellationReason?: string;
  cancellationComments?: string;
}

export interface UnmapOtherDocumentsPayload {
  clientId: string;
  otherDocumentId: string;
}
export interface UnmapContractDocumentsPayload {
  clientId: string;
  contractDocumentId: string;
}

export interface UpdateStatusOtherDocumentParams {
  documentId: string;
  status: string;
}

export interface ArchiveStatusOtherDocumentParams {
  archivalReason: string;
  archivalComments: string;
  documentId: string;
}

export interface DocumentDetailsContract {
  id?: string;
  clientProfileId?: string;
  opportunityId?: string;
  opportunityName?: string;
  contractId?: string;
  ownershipCountryId?: string;
  ownershipCountry?: Country;
  exclusivityId?: string;
  sowAutoRenewalClauseId?: string;
  sowAutoRenewalTerms?: number;
  sowStatusId: string;
  sowExpirationDate?: string;
  classificationId?: string;
  serviceLevelAgreementsId?: string;
  customDeliverablesId?: string;
  paymentTerms?: IdNameList;
  contractDuration?: number;
  engagementTypeId?: string;
  sowContractEffectiveDate?: string;
  uploadDate: string;
  contractGeoScopeId: string;
  notes?: string;
  documentName?: string;
  documentId?: string;
  contractGeoScopeCountries: string[] | Country[];
  documentType?: string;
  productLineId?: string[];
  status?: string;
  isCancelled?: boolean;
  productLines?: IdNameList[];
}

export interface DocumentDetailsOthers {
  id?: string;
  clientProfileId?: string;
  statusId: StatusBadgeType;
  uploadDate: string;
  effectiveDate?: string;
  notes?: string;
  documentId?: string;
  documentName?: string;
  documentType?: string;
  status?: StatusBadgeType;
}

export interface ProgramTemplateActivityParams {
  programTemplateId: string;
  activityId: string;
}

export interface ProgramTemplateDeliverableParams {
  programTemplateId: string;
  deliverableId: string;
}

export interface ResourcesAndProgramTemplatesParams {
  clientId: string;
  groupName: string;
  resources: Resource[];
  programTemplates: Partial<ProgramTemplate>[];
}

export interface UpdateResourcesAndProgramTemplatesParams
  extends ResourcesAndProgramTemplatesParams {
  groupId: string;
  clientProfileId: string;
}

export interface GetDocumentListParams {
  clientId: string;
  documentType: DocumentTypeGetAPI;
}

export interface DeleteClientSpecificContentParams {
  clientResourceGroupId: string;
}

type DocumentPayloadType<T extends DocumentType> = T extends "client_others"
  ? DocumentDetailsOthers
  : DocumentDetailsContract;

export interface DocumentUploadParams<T extends DocumentType> {
  clientId: string;
  documentType: T;
  documentPayload: DocumentPayloadType<T>;
}

export interface DocumentUpdateParams<T extends DocumentType> {
  id: string;
  documentType: T;
  documentPayload: DocumentPayloadType<T>;
}

export interface SearchInheritDocumentsPayload {
  statusIds: string[];
  clientId: string;
  searchInParentsOnly: boolean;
  contractId?: number;
  documentName?: string;
  documentType?: string;
}

export interface SearchDocumentResponse {
  clientName: string;
  clientCode: string;
  document: IdNameList;
  contractId: number;
  documentId: string; // This id will be used for downloading the blob data of the document
  documentType: string;
  statusId: string;
  effectiveDate: string;
  expirationDate: string;
  isInherited: boolean;
}

export interface SearchDocumentResultRow extends SearchDocumentResponse {
  isChecked?: boolean;
  mappedContractId?: string;
  [key: string]: any;
}

export type SearchDocumentListDropdownFilterKey =
  | typeof InheritDocumentsResultFormKey.documentStatus
  | typeof InheritDocumentsResultFormKey.documentType;

interface Document {
  documentId: string;
  documentType: string;
}
export interface InheritDocumentsPayload {
  clientId: string;
  documents: Document[];
}

export interface GetCountriesByGeoScopeParams {
  geoScopeId: string;
}

export type FileOperationType = "add" | "update";

export interface FileUploadParams {
  entityId?: string; // clientId, programId, etc based on the entityType.
  file: any;
  documentType: DocumentType;
  operationType: FileOperationType;
  entityType: string;
}

export interface FileUploadResponse {
  fileName: string;
  success: boolean;
  documentId: string;
}

export interface FileDownloadParams {
  documentId: string;
  documentType: string;
}

export interface InvoiceDownloadParams {
  invoiceId: string;
  deliverableFrameworkType: string;
}

export interface AddPurchaseOrderParams {
  clientId: string;
  poNumber: string;
  effectiveDate: string;
  currencyTypeId: string;
  initialAmount: string;
  expiryDate?: string;
  statusId?: string;
  notes?: string;
}

export interface UpdatePurchaseOrderParams extends AddPurchaseOrderParams {
  purchaseOrderId: string;
  reOpenReason?: string;
}

export interface ReopenPurchaseOrderParams {
  purchaseOrderId: string;
  reOpenReason?: string;
  expiryDate?: string;
  clientId?: string;
  action?: string;
}

export interface GetPurchaseOrderResponse {
  clientId: string;
  purchaseOrderId: string;
  poNumber: string;
  effectiveDate: string;
  statusId: StatusBadgeType;
  currencyTypeId: string;
  currentAmount: string;
  notes?: string;
  expiryDate?: string;
  createdTimeStamp: string;
  reOpenDate?: string;
  reOpenReason?: string;
}

export interface GetPurchaseOrderParams {
  clientCode: string;
  statusId?: string;
}

export const POTransactionType = {
  Add: 0,
  TopUp: 1,
  Deduct: 2,
} as const;

export type OperationType =
  (typeof POTransactionType)[keyof typeof POTransactionType];

export interface Transactions {
  operationId: OperationType;
  startBalanceAmount: number;
  adjustmentAmount?: number;
  totalBalanceAmount: number;
  createdTimeStamp: string;
  transactionType: string;
  project?: {
    id: string;
    key: string;
  };
  program?: {
    id: string;
    key: string;
  };
  [key: string]: any;
}

export interface POAdjustmentParams {
  purchaseOrderId: string;
  operationId: OperationType;
  adjustmentAmount: number;
}

export interface SalesOfficeByLocalClientRelationshipOwnerIdResponse {
  salesOfficeId: string;
  name: string;
}

export interface DeletePurchaseOrderParams {
  purchaseOrderId: string;
}

export interface CreateClientActivityPayload {
  id?: string;
  activityTypeId: string;
  modalities: string[];
  localizedTopicNames: Locale[];
  // activityClass: string;
  activityStatus: string;
  units: string;
  candidateStatuses: string[];
  deliverableIds: string[];
  triggerBilling: boolean;
  countsTowardsLastContactDate: boolean;
  triggerTouchPointSurvey: boolean;
  measurement: string;
}

export interface CreateDeliverableParams {
  deliverableType: string;
  units: string;
  measurements: string;
  localizedDeliverableNames: { name: string; localeKey: string }[];
}

export interface AddExistingDeliverableParams {
  deliverableIds: string[];
}

export interface DeleteCompatibleProgramTemplateParams {
  programTemplateId: string;
  compatibleProgramTemplateId: string;
}

export type ParticipantsType = {
  candidateId: string;
  firstName: string;
  lastName: string;
  emailId: string;
  programEndDate: string;
  status: StatusBadgeType;
  deliveryOfficeId: string;
  deliveryOfficeName: string;
  [key: string]: any;
};

export type FeaturesType = {
  id: string;
  name: string;
  type?: string;
  siteCoreKey: string;
  isChecked?: boolean;
};

export type FeaturesSection = {
  title?: string;
  items: FeaturesType[];
};

export interface SearchGenericAccountsParams {
  accountName: string;
  purposeId: string;
  clientId: string;
  solutionOfferingId: string;
  programClassId: string;
  countryId: string | null;
  deliveryOfficeIds: string[];
  statusId: string;
  requestedBy: string | null;
}

export interface GenericAccountSearchResponse {
  id: string;
  registrationCode: string;
  numberOfAccounts: {
    assigned: number;
    registered: number;
  };
  name: string;
  purposeId: string;
  client: IdNameList;
  startDate: string;
  expirationDate: string;
  programDuration: number;
  requestedBy: string;
  solutionOffering: IdNameList;
  country: IdNameList;
  deliveryOffices: IdNameList[];
  programClass: IdNameList;
  status: StatusBadgeType;
  requestedByUserId: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  [key: string]: any;
}

export interface GetGenericAccountsResponse
  extends GenericAccountSearchResponse {
  features: FeaturesType[];
  participants: ParticipantsType[];
}

export interface GenericAccountKeyDetails {
  registrationCode: string;
  assigned: number;
  registered: number;
  status: StatusBadgeType;
}

export interface FeatureListParams {
  programClassId: string;
  countryId: string;
}

export interface FeatureParams {
  id: string;
  type?: string;
}

export interface CreateGenericAccountParams {
  name: string;
  purposeId: string;
  clientId?: string;
  registrationCode: string;
  startDate: string;
  expirationDate: string;
  numberOfAccounts: string;
  programDuration: string;
  requestedBy?: string;
  requestedByUserId?: string;
  solutionOfferingId: string;
  programClassId: string;
  countryId: string;
  deliveryOfficeIds: string[];
  status: string;
  features?: FeatureParams[];
}

export interface UpdateGenericAccountParams extends CreateGenericAccountParams {
  genericAccountId: string;
}

export interface UpdateParticipantDataParams {
  participantId: string;
  programEndDate?: string;
  platformDetails?: {
    timeZoneId?: string;
    city?: string;
    languagePreferenceId?: string;
  };
  contactPreferences: ContactPreferences;
}

export interface AlternateDisplayNameQueryParams {
  clientId: string;
  programTemplateId?: string;
}

export interface AlternateDisplayNameApiResponse {
  name: string;
}

export interface GenericAccountParticipantResponse {
  participantId: string;
  participantInformation: {
    firstName: string;
    lastName: string;
    preferredName: string;
    email: string;
    alternateEmail: string;
    preferredContactNumber: string; // phone number value
    preferredContactNumberISOCode: string; // country code value, eg: +91, +1
  };
  programInformation: {
    platformPersona: string;
    programEndDate: string;
    isJobLanded: boolean;
  };
  platformDetails: {
    timeZone: IdNameList;
    city: string;
    countryCode: string;
    languagePreferences: LanguagePreference[];
  };
  contactPreferences: ContactPreferences;
}

export type GetInvoicingLanguagesParams = {
  programId?: string;
  clientId?: string;
};

export interface GetClientListParams {
  keyword: string;
  isRefetch?: boolean;
  isActiveClientOnly?: boolean;
  excludeNCBPClient?: boolean;
  clientType?: ClientTypeEnum;
}

export interface GetClientListResponse {
  id: string;
  name: string;
  clientCode: string;
  clientName: string;
  clientType: string;
}

export interface ExistingDeliverableParams {
  frameworkType: string;
  isRefetch?: boolean;
}

export interface GetContractDocumentPayload {
  clientId: string;
  productLineId: string;
  status: string;
  sortBy: string;
}

export interface GetContractDocumentResponse {
  id: string;
  contractId: string;
  name: string;
  opportunityId: string;
  opportunityName: string;
  paymentTermId: string;
  paymentTermName: string;
}

export interface ValidateOpportunityPayload {
  opportunityId: string;
  clientId: string;
  contractDocumentId: string;
  programId: string;
  isForDocument: boolean;
  isForProgram: boolean;
}

export interface GetClientSalesOfficesListResponse {
  isSelected: boolean;
  id: string;
  name: string;
  billingOffice: IdNameList;
}
